import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import { useAppScrollDirection } from '../../../app/features/scroll-direction/hooks/useAppScrollDirection';
import useInvestFunds from '../hooks/fund/useInvestFunds';
import IFund from '../interfaces/fund/IFund';
import AppError404 from '../../../app/features/error/404/AppError404';
import AppLoaderContent from '../../../app/features/loader/content/AppLoaderContent';
import CInvestDisclaimer from '../components/CInvestDisclaimer';
import { Element, scroller } from 'react-scroll';
import { DelimitedNumericArrayParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import IFundInvestor from '../interfaces/IFundInvestor';
import CAppFilterSelect from '../../../app/components/CAppFilterSelect';
import investmentFundLang from '../langs/investmentFundLang';
import CAppInfoTooltip from '../../../app/components/CAppInfoTooltip';
import IInvestor from '../interfaces/IInvestor';
import { Helmet } from 'react-helmet';
import AppCustomIcon from '../../../app/features/icon/AppIcon';
import VInvestFundPerformance from '../views/fund/VInvestFundPerformance';
import VInvestFundQuestion from '../views/fund/VInvestFundQuestion';
import CDateUpdatedAtom from '../components/CDateUpdatedAtom';
import CAppTitle from '../../../app/components/CAppTitle';
import _ from 'lodash';
import VInvestFundOperations from '../views/fund/VInvestFundOperations';
import VInvestFundDocuments from '../views/fund/VInvestFundDocuments';
import VInvestFundInformations from '../views/fund/VInvestFundInformations';
import IInvestmentFundOnglet from '../interfaces/fund/IInvestmentFundOnglet';
import IFundType from '../interfaces/expertise/IFundType';
import investExpertiseSynthesisVentureCapitalLang from '../features/fund-type/langs/investExpertiseSynthesisVentureCapitalLang';
import useInvestExpertises from '../hooks/expertise/useInvestExpertises';
import { useAppNavigationContext } from '../../../app/features/navigation/providers/AppNavigationProvider';
import VInvestFundAssetsList from '../views/fund/VInvestFundAssetsList';
import VInvestFundAssetsTable from '../views/fund/VInvestFundAssetsTable';
import useInvestFundAssets from '../hooks/fund/useInvestFundAssets';
import VInvestFundAssetsListListedAssets from '../views/fund/VInvestFundAssetsListListedAssets';
import useInvestFundAssetsSG from '../hooks/fund/useInvestFundAssetsSG';

const PInvestFund = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const routeParams = useParams();
	const authContext = useAuthContext();
	const refPerformance = useRef();
	const refDocuments = useRef();
	const refOperations = useRef();
	const refInformations = useRef();
	const refAssets = useRef();

	const investExpertises = useInvestExpertises();

	const navigationContext = useAppNavigationContext();

	useEffect(() => {
		if (navigationContext.displayCurrencySelector === true) {
			navigationContext.setDisplayCurrencySelector(false);
		}
	}, [navigationContext]);

	const appScrollDirection = useAppScrollDirection();

	const [blocks, setBlocks] = useState<IInvestmentFundOnglet[] | null>(null);

	useEffect(() => {
		if (
			blocks === null &&
			refPerformance &&
			refPerformance.current &&
			refDocuments &&
			refDocuments.current &&
			refOperations &&
			refOperations.current &&
			refInformations &&
			refInformations.current &&
			refAssets &&
			refAssets.current
		) {
			const blocksTmp: IInvestmentFundOnglet[] = [];
			blocksTmp.push({
				tag: 'performance',
				minY: refPerformance.current['offsetTop'],
				maxY: refPerformance.current['offsetTop'] + refPerformance.current['offsetHeight'],
			});
			blocksTmp.push({
				tag: 'documents',
				minY: refDocuments.current['offsetTop'],
				maxY: refDocuments.current['offsetTop'] + refDocuments.current['offsetHeight'],
			});
			blocksTmp.push({
				tag: 'operations',
				minY: refOperations.current['offsetTop'],
				maxY: refOperations.current['offsetTop'] + refOperations.current['offsetHeight'],
			});
			blocksTmp.push({
				tag: 'informations',
				minY: refInformations.current['offsetTop'],
				maxY: refInformations.current['offsetTop'] + refInformations.current['offsetHeight'],
			});
			blocksTmp.push({
				tag: 'assets',
				minY: refAssets.current['offsetTop'],
				maxY: refAssets.current['offsetTop'] + refAssets.current['offsetHeight'],
			});

			setBlocks(blocksTmp);
		}
	}, [appScrollDirection, blocks]);

	const { funds: allFunds } = useInvestFunds();

	const [fund, setFund] = useState<IFund | null | undefined>(undefined);
	const [fundType, setFundType] = useState<IFundType | null>(null);
	useEffect(() => {
		if (routeParams.id && allFunds && investExpertises) {
			const currentFund = allFunds.find((f) => f.id === Number.parseInt(routeParams.id ?? '')) as IFund;
			if (currentFund !== undefined) {
				setFundType(investExpertises.find((x) => x.tag === currentFund.typeTag) ?? null);
				setFund(currentFund);
			} else {
				setFund(null);
			}
		} else {
			setFundType(null);
			setFund(undefined);
		}
	}, [routeParams.id, allFunds, investExpertises]);

	const { fundAssets } = useInvestFundAssets(fund ? fund.id : null);
	const { fundAssets: fundAssetsSG } = useInvestFundAssetsSG(fund ? fund.id : null);

	const queryParamsDefaultValue = {
		view: withDefault(StringParam, 'performance'),
		investorIds: withDefault(DelimitedNumericArrayParam, null),
	};

	const [queryParams, setQueryParams] = useQueryParams(queryParamsDefaultValue);
	const [filters, setFilters] = useState<{ investorIds?: number[] }>({});
	useEffect(() => {
		if (queryParams) {
			setFilters({
				investorIds: (queryParams.investorIds as number[] | null) ?? undefined,
			});
		}
	}, [queryParams]);

	const [dataUpdatedOn, setDataUpdatedOn] = useState<Date>();

	// Options sub portfolio
	const [optionsInvestors, setOptionsInvestors] = useState<{ id: number; name: string }[] | null>(null);
	useEffect(() => {
		if (fund && fund.investors) {
			const investorsTmp = _.cloneDeep(fund.investors).map((x: IFundInvestor) => ({
				id: x.company.id,
				name: x.company.name,
			}));
			setOptionsInvestors(_.orderBy(investorsTmp, ['name'], ['asc']));
		} else {
			setOptionsInvestors(null);
		}
	}, [fund]);

	const [ongletSelected, setOngletSelected] = useState<string | null>(null);

	useEffect(() => {
		if (ongletSelected === null && queryParams && queryParams.view !== null) {
			setOngletSelected(queryParams.view);
		}
	}, [ongletSelected, queryParams]);

	const handleSelectOnglet = (_event: React.SyntheticEvent, newValue: string) => {
		setQueryParams((prevData) => ({
			...prevData,
			view: newValue,
		}));
	};

	useEffect(() => {
		const scrollHandler = () => {
			if (!blocks) return;
			const semiHeight = window.innerHeight / 2;
			// On considère que le champ de vision est situé au milieu de l'écran
			const currentBlock = blocks.find(
				(x) => x.minY <= window.pageYOffset + semiHeight && x.maxY >= window.pageYOffset + semiHeight,
			);

			if (currentBlock && ongletSelected !== currentBlock.tag) {
				setOngletSelected(currentBlock.tag);
			}
		};

		if (blocks) {
			window.addEventListener('scroll', scrollHandler);
		}

		return () => window.removeEventListener('scroll', scrollHandler);
	}, [blocks, ongletSelected, setQueryParams]);

	const componentFilterInvestorSelect = fundType?.tag !== 'listed_assets' && (
		<CAppFilterSelect
			className="fund-detail-subportfolio"
			multiple
			multipleRenderTags={(val: IFund[]) => (
				<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
					{t(investmentFundLang.x_sub_portfolios.tag, { count: val ? val.length : 0 })}
				</Box>
			)}
			label={
				<>
					{t(investmentFundLang.label_all_sub_portfolios.tag)}
					<CAppInfoTooltip title={t(investmentFundLang.label_all_sub_portfolios_tooltip.tag)} />
				</>
			}
			value={
				optionsInvestors && filters
					? optionsInvestors.filter((x) => filters.investorIds && filters.investorIds.includes(x.id))
							.length === 0
						? optionsInvestors
						: optionsInvestors.filter((x) => filters.investorIds && filters.investorIds.includes(x.id))
					: null
			}
			onChange={(val: IInvestor[]) =>
				setQueryParams({ ...queryParams, investorIds: val && val.length > 0 ? val.map((x) => x.id) : null })
			}
			size="small"
			options={optionsInvestors}
		/>
	);

	return (
		<>
			{fund && fund.id && fundAssets && fundAssetsSG && fundType ? (
				<>
					<Helmet>
						<title>
							{authContext.authContext === 'employee' ? '[E] ' : ''} {fund.name} -{' '}
							{authContext.currentCompany ? authContext.currentCompany.name : ''}
						</title>
					</Helmet>
					<Grid container spacing={3} alignItems="center" justifyItems="center">
						<Grid item sm={9} xs={12}>
							<Box>
								<Box
									sx={{
										fontWeight: 600,
										fontSize: '1.125rem',
										lineHeight: '1.5',
										color: theme.palette.primary.main,
									}}
								>
									{fundType ? t(fundType.name) : ''}
								</Box>
								<CAppTitle
									title={
										// Si VC ou PE on affiche l'info (Fund ou Co-Investment) sinon rien
										fundType.tag === 'private_credit'
											? fund.name
											: (fund.isCoinvestment
													? t(
															investExpertiseSynthesisVentureCapitalLang
																.title_coinvestment.tag,
													  )
													: t(investExpertiseSynthesisVentureCapitalLang.title_fund.tag)) +
											  ' : ' +
											  fund.name
									}
									subtitle={
										fundType.tag !== 'listed_assets' && (
											<>
												<CDateUpdatedAtom date={dataUpdatedOn} />
											</>
										)
									}
								/>
							</Box>
						</Grid>
						<Grid item sm={3} xs={12} sx={{ textAlign: 'right' }}>
							<Box>{componentFilterInvestorSelect}</Box>
						</Grid>
					</Grid>
					<Box
						sx={{
							zIndex: '1050',
							position: { sm: 'sticky', xs: 'initial' },
							top: { sm: '60px', xs: '0px' },
							paddingBottom: '10px',
							backgroundColor: theme.palette.background.default,
						}}
					>
						<Tabs
							value={ongletSelected}
							onChange={handleSelectOnglet}
							variant="scrollable"
							allowScrollButtonsMobile
							scrollButtons={isMobile ? true : false}
							sx={{ marginTop: '35px', marginBottom: '0px' }}
						>
							<Tab
								className="fund-tabs"
								id="fund-tabs-performance"
								value="performance"
								disabled={!fundType.fundOnglets.includes('performance')}
								sx={{
									display: !fundType.fundOnglets.includes('performance') ? 'none' : '',
								}}
								icon={<AppCustomIcon tag="pie_chart" className="icon" />}
								iconPosition="start"
								label={t(investmentFundLang.onglet_performance.tag)}
								onClick={() =>
									!isMobile
										? scroller.scrollTo('scroll-performance', {
												delay: 0,
												smooth: true,
												offset: -125,
										  })
										: null
								}
							/>

							<Tab
								className="fund-tabs"
								id="fund-tabs-documents"
								value="documents"
								disabled={!fundType.fundOnglets.includes('documents')}
								sx={{
									display: !fundType.fundOnglets.includes('documents') ? 'none' : '',
								}}
								icon={<AppCustomIcon tag="folders" />}
								iconPosition="start"
								label={t(investmentFundLang.onglet_documents.tag)}
								onClick={() =>
									!isMobile
										? scroller.scrollTo('scroll-documents', {
												delay: 0,
												smooth: true,
												offset: -125,
										  })
										: null
								}
							/>

							<Tab
								className="fund-tabs"
								id="fund-tabs-operations"
								value="operations"
								disabled={!fundType.fundOnglets.includes('operations')}
								sx={{
									display: !fundType.fundOnglets.includes('operations') ? 'none' : '',
								}}
								icon={<AppCustomIcon tag="fund_operations" />}
								iconPosition="start"
								label={t(investmentFundLang.onglet_operations.tag)}
								onClick={() =>
									!isMobile
										? scroller.scrollTo('scroll-operations', {
												delay: 0,
												smooth: true,
												offset: -125,
										  })
										: null
								}
							/>

							<Tab
								className="fund-tabs"
								id="fund-tabs-informations"
								value="informations"
								disabled={!fundType.fundOnglets.includes('informations')}
								sx={{
									display: !fundType.fundOnglets.includes('informations') ? 'none' : '',
								}}
								icon={<AppCustomIcon tag="fund_informations" />}
								iconPosition="start"
								label={t(investmentFundLang.onglet_informations_contacts.tag)}
								onClick={() =>
									!isMobile
										? scroller.scrollTo('scroll-informations', {
												delay: 0,
												smooth: true,
												offset: -125,
										  })
										: null
								}
							/>

							<Tab
								className="fund-tabs"
								id="fund-tabs-assets"
								value="assets"
								disabled={!fundType.fundOnglets.includes('assets')}
								sx={{
									display:
										fundType.fundOnglets.includes('assets') &&
										(fundAssets?.length || fundAssetsSG?.assetsFund?.length)
											? ''
											: 'none',
								}}
								icon={<AppCustomIcon tag="fund_assets" />}
								iconPosition="start"
								label={t(investmentFundLang.onglet_informations_assets.tag)}
								onClick={() =>
									!isMobile
										? scroller.scrollTo('scroll-assets', {
												delay: 0,
												smooth: true,
												offset: -125,
										  })
										: null
								}
							/>
						</Tabs>
					</Box>

					<Box ref={refPerformance}>
						{fundType.fundOnglets.includes('performance') &&
						(!isMobile || queryParams.view === 'performance' || queryParams.view === undefined) ? (
							<>
								<Element name="scroll-performance" />

								<VInvestFundPerformance
									fund={fund}
									filters={filters}
									setDataUpdatedOn={setDataUpdatedOn}
								/>
								{fund.typeTag !== 'listed_assets' && (
									<VInvestFundQuestion fund={fund} filters={filters} />
								)}
							</>
						) : (
							''
						)}
					</Box>
					<Box ref={refDocuments}>
						{fundType.fundOnglets.includes('documents') &&
						(!isMobile || queryParams.view === 'documents') ? (
							<>
								<Element name="scroll-documents" />

								<VInvestFundDocuments fund={fund} filters={filters} />
							</>
						) : (
							''
						)}
					</Box>
					<Box ref={refOperations}>
						{fundType.fundOnglets.includes('operations') &&
						(!isMobile || queryParams.view === 'operations') ? (
							<>
								<Element name="scroll-operations" />

								<VInvestFundOperations fund={fund} filters={filters} />
							</>
						) : (
							''
						)}
					</Box>
					<Box ref={refInformations}>
						{fundType.fundOnglets.includes('informations') &&
						(!isMobile || queryParams.view === 'informations') ? (
							<>
								<Element name="scroll-informations" />

								<VInvestFundInformations fund={fund} filters={filters} />
							</>
						) : (
							''
						)}
					</Box>

					<Box ref={refAssets}>
						{fundType.fundOnglets.includes('assets') && (!isMobile || queryParams.view === 'assets') ? (
							<>
								<Element name="scroll-assets" />
								{fundType.tag === 'listed_assets' ? (
									<VInvestFundAssetsListListedAssets fund={fund} />
								) : fundType.assetsDisplay === 'list' ? (
									<VInvestFundAssetsList fund={fund} filters={filters} />
								) : fundType.assetsDisplay === 'table' ? (
									<VInvestFundAssetsTable fund={fund} filters={filters} />
								) : (
									<></>
								)}
							</>
						) : (
							''
						)}
					</Box>

					<CInvestDisclaimer />
				</>
			) : fund === null ? (
				<AppError404 variant="container" />
			) : (
				<AppLoaderContent />
			)}
		</>
	);
};

export default PInvestFund;
